// Title, artist and cover art from the file's tags (F7.2). Tag reading never blocks an import:
// any failure just means "no tags", and the file name is used instead.

export interface FileTags {
  title?: string;
  artist?: string;
  /** Small JPEG data URL (fits localStorage: a few KB). */
  cover?: string;
}

const THUMB_SIZE = 72;

function clean(value: string | undefined): string | undefined {
  const v = value?.trim();
  return v === '' ? undefined : v;
}

/** Center-cropped square thumbnail as a JPEG data URL. */
async function thumbnail(data: Uint8Array, format: string): Promise<string | undefined> {
  const bitmap = await createImageBitmap(new Blob([data as BlobPart], { type: format }));
  const side = Math.min(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = THUMB_SIZE;
  canvas.height = THUMB_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return undefined;
  ctx.drawImage(
    bitmap,
    (bitmap.width - side) / 2,
    (bitmap.height - side) / 2,
    side,
    side,
    0,
    0,
    THUMB_SIZE,
    THUMB_SIZE,
  );
  bitmap.close();
  return canvas.toDataURL('image/jpeg', 0.75);
}

export async function readTags(file: File): Promise<FileTags> {
  try {
    // Loaded on demand: only needed while importing.
    const { parseBlob, selectCover } = await import('music-metadata');
    const meta = await parseBlob(file, { duration: false });
    const picture = selectCover(meta.common.picture);
    const tags: FileTags = {};
    const title = clean(meta.common.title);
    const artist = clean(meta.common.artist);
    if (title) tags.title = title;
    if (artist) tags.artist = artist;
    if (picture) {
      const cover = await thumbnail(picture.data, picture.format).catch(() => undefined);
      if (cover) tags.cover = cover;
    }
    return tags;
  } catch {
    return {};
  }
}
